import type { Theme } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    FormButtonRow,
    FormSection,
    Icon,
    IconButton,
    Row,
    Select,
    Text,
    Tooltip,
} from '@ksyos/design-system';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import type { IFundingSetting, IServiceOrganizationNotificationSettings } from '@common/interfaces';
import type { Uuid } from '@common/utils/uuid';
import type { FundingServicePart } from '@frontend/services/api/fundingServicePartsApiService';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { fundingSettingsAddSchema, fundingSettingsEditSchema } from '@common/validation/fundingSettings';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { Currency, InputCurrency } from '@frontend/components/elements/common';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { CheckboxInput } from '@frontend/components/fields';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getFundingServiceParts } from '@frontend/services/api/fundingServicePartsApiService';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, getValidationErrorTranslation, hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import {
    emptyValidationErrors,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils/validation';

interface IServiceOrganizationSettingsWithFundingProps {
    onSubmit: () => void;
    serviceOrganizationSetting: IServiceOrganizationNotificationSettings;
}

interface IFundingSettingWithErrors extends IFundingSetting {
    errors: ValidationErrors;
}

export function FundingSettingAddEdit(props: IServiceOrganizationSettingsWithFundingProps) {
    assertHasAuthorization(ManagerAuthorization.organizationRead);

    const abortSignal = useAbortSignal();
    const abortableFetch = useAbortableFetch();
    const [fundingSettings, setFundingSettings] = useState<IFundingSettingWithErrors[]>([]);
    const [oldFundingSettings, setOldFundingSettings] = useState<IFundingSetting[]>([]);
    const [fundingServiceParts, setFundingServiceParts] = useState<FundingServicePart[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

    useEffect(() => {
        load();
    }, [props.serviceOrganizationSetting.id]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    canEdit && !isEditing ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={`${props.serviceOrganizationSetting.service.name} (${DateTime.from(
                    props.serviceOrganizationSetting.startDate
                ).format('DD-MM-YYYY')} - ${
                    props.serviceOrganizationSetting.endDate
                        ? DateTime.from(props.serviceOrganizationSetting.endDate).format('DD-MM-YYYY')
                        : 'nu'
                })`}
            />
            <Card.Body>
                <Form submitOnEnter={false}>
                    <FormSection>
                        <Row
                            columnGap={2}
                            rowGap={4}
                        >
                            <Col span={2}>
                                <Text
                                    color="subtle"
                                    size="xs"
                                >
                                    {translateKey('service.compensation')}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text
                                    color="subtle"
                                    size="xs"
                                >
                                    {translateKey('common.startDateAndEndDate')}
                                </Text>
                            </Col>
                            <Col span={1}>
                                <Text
                                    color="subtle"
                                    size="xs"
                                >
                                    {translateKey('fundingSettings.invoice')}{' '}
                                </Text>
                                {renderTooltip(translateKey('fundingSettings.invoiceExplanation'))}
                            </Col>
                            <Col span={1}>
                                <Text
                                    color="subtle"
                                    size="xs"
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {translateKey('fundingSettings.alternativeFunding')}{' '}
                                </Text>
                                {renderTooltip(translateKey('fundingSettings.alternativeFundingExplanation'))}
                            </Col>
                            <Col span={1}>
                                <Text
                                    color="subtle"
                                    size="xs"
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {translateKey('fundingSettings.alreadyFunded')}{' '}
                                </Text>
                                {renderTooltip(translateKey('fundingSettings.alreadyFundedExplanation'))}
                            </Col>
                            {fundingServiceParts.length > 0 && (
                                <Col span={1}>
                                    <Text
                                        color="subtle"
                                        size="xs"
                                    >
                                        {translateKey('finance.fundingServicePart.shortName')}
                                    </Text>
                                    {renderTooltip(translateKey('finance.fundingServicePart.longName'))}
                                </Col>
                            )}
                            <Col span={1}></Col>
                        </Row>
                    </FormSection>

                    {fundingSettings.map((fundingSetting, index) => {
                        return (
                            <FormSection key={index}>
                                <Row
                                    columnGap={4}
                                    rowGap={4}
                                >
                                    {!isEditing && (
                                        <>
                                            <Col span={2}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    <Currency value={fundingSetting.compensationCharge} />
                                                </Text>
                                            </Col>
                                            <Col span={5}>
                                                <Box
                                                    alignItems="center"
                                                    display="flex"
                                                    flexDirection={['column', 'row']}
                                                    gap={2}
                                                >
                                                    <Text
                                                        as="label"
                                                        fontWeight="medium"
                                                    >
                                                        <NullableFormattedDate value={fundingSetting.startDate} />
                                                    </Text>
                                                    <Text
                                                        as="label"
                                                        fontWeight="medium"
                                                        size="sm"
                                                    >
                                                        {translateKey('common.until')}
                                                    </Text>
                                                    <Text
                                                        as="label"
                                                        fontWeight="medium"
                                                    >
                                                        {fundingSetting.endDate ? (
                                                            <NullableFormattedDate value={fundingSetting.endDate} />
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </Text>
                                                </Box>
                                            </Col>
                                            <Col span={1}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    {fundingSetting.sendInvoice ? 'ja' : 'nee'}
                                                </Text>
                                            </Col>
                                            <Col span={1}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    {fundingSetting.isAlternativeFinancing ? 'ja' : 'nee'}
                                                </Text>
                                            </Col>
                                            <Col span={1}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    {fundingSetting.financedByPreviousService ? 'ja' : 'nee'}
                                                </Text>
                                            </Col>
                                            {fundingServiceParts.length > 0 && (
                                                <Col span={1}>
                                                    <Text
                                                        as="label"
                                                        fontWeight="medium"
                                                    >
                                                        {fundingSetting.sendInvoice
                                                            ? mapFundablePartIdToName(
                                                                  fundingSetting.fundingServicePartId
                                                              )
                                                            : '-'}
                                                    </Text>
                                                </Col>
                                            )}
                                            <Col span={1}></Col>
                                        </>
                                    )}

                                    {isEditing && (
                                        <>
                                            <Col span={2}>
                                                <Box display="flex">
                                                    <Text
                                                        as="label"
                                                        mt={2.5}
                                                        pt={2.5}
                                                        size="xs"
                                                        sx={{
                                                            alignItems: 'center',
                                                            display: 'flex',
                                                            marginRight: '4px',
                                                            marginTop: '0px',
                                                            paddingTop: '0px',
                                                            color: !fundingSetting.sendInvoice
                                                                ? (theme: Theme) => theme.colors.gray500
                                                                : undefined,
                                                        }}
                                                    >
                                                        €
                                                    </Text>
                                                    <InputCurrency
                                                        automation={`compensatie-${props.serviceOrganizationSetting.id}-${index}`}
                                                        disabled={!fundingSetting.sendInvoice}
                                                        error={fundingSetting.errors.get('compensationCharge')}
                                                        onChange={(value) =>
                                                            handleChange('compensationCharge', value, index)
                                                        }
                                                        onDoValidate={() => {
                                                            return;
                                                        }}
                                                        value={fundingSetting.compensationCharge}
                                                    />
                                                </Box>
                                            </Col>
                                            <Col span={6}>
                                                <Box
                                                    display="flex"
                                                    flexDirection={['column', 'column', 'row']}
                                                    gap={1}
                                                >
                                                    <Box>
                                                        <DatePicker
                                                            error={getValidationErrorTranslation(
                                                                fundingSetting.errors.get('startDate')
                                                            )}
                                                            id={`automation-begindatum-${props.serviceOrganizationSetting.id}-${index}`}
                                                            maxValue={
                                                                props.serviceOrganizationSetting.endDate
                                                                    ? convertDateValue(
                                                                          props.serviceOrganizationSetting.endDate
                                                                      ) || undefined
                                                                    : undefined
                                                            }
                                                            onChange={(value) => {
                                                                if (value) {
                                                                    handleChange(
                                                                        'startDate',
                                                                        DateTime.from(value).format('YYYY-MM-DD'),
                                                                        index
                                                                    );
                                                                }
                                                            }}
                                                            size="medium"
                                                            value={
                                                                convertDateValue(fundingSetting.startDate) || new Date()
                                                            }
                                                        />
                                                    </Box>
                                                    <Text
                                                        as="label"
                                                        size="xs"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {translateKey('common.until')}
                                                    </Text>
                                                    <Box>
                                                        <DatePicker
                                                            error={getValidationErrorTranslation(
                                                                fundingSetting.errors.get('endDate')
                                                            )}
                                                            id={`automation-einddatum-${props.serviceOrganizationSetting.id}-${index}`}
                                                            maxValue={
                                                                props.serviceOrganizationSetting.endDate
                                                                    ? convertDateValue(
                                                                          props.serviceOrganizationSetting.endDate
                                                                      ) || undefined
                                                                    : undefined
                                                            }
                                                            minValue={
                                                                convertDateValue(fundingSetting.startDate) || undefined
                                                            }
                                                            onChange={(value) => {
                                                                if (value) {
                                                                    handleChange(
                                                                        'endDate',
                                                                        DateTime.from(value).format('YYYY-MM-DD'),
                                                                        index
                                                                    );
                                                                } else {
                                                                    handleChange('endDate', null, index);
                                                                }
                                                            }}
                                                            size="medium"
                                                            value={convertDateValue(fundingSetting.endDate)}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Col>

                                            <Col
                                                span={1}
                                                style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                <CheckboxInput
                                                    automation={`factuur-${props.serviceOrganizationSetting.id}-${index}`}
                                                    checked={fundingSetting.sendInvoice}
                                                    error={fundingSetting.errors.get('sendInvoice')}
                                                    onChange={(value) => {
                                                        handleChangeFundingMethod({ sendInvoice: value }, index);
                                                    }}
                                                />
                                            </Col>
                                            <Col
                                                span={1}
                                                style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                <CheckboxInput
                                                    automation={`alternative-financing-${props.serviceOrganizationSetting.id}-${index}`}
                                                    checked={fundingSetting.isAlternativeFinancing}
                                                    error={fundingSetting.errors.get('isAlternativeFinancing')}
                                                    onChange={(value) => {
                                                        handleChangeFundingMethod(
                                                            { isAlternativeFinancing: value },
                                                            index
                                                        );
                                                    }}
                                                />
                                            </Col>
                                            <Col
                                                span={1}
                                                style={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                <CheckboxInput
                                                    automation={`previously-financed-${props.serviceOrganizationSetting.id}-${index}`}
                                                    checked={fundingSetting.financedByPreviousService}
                                                    error={fundingSetting.errors.get('financedByPreviousService')}
                                                    onChange={(value) =>
                                                        handleChange('financedByPreviousService', value, index)
                                                    }
                                                />
                                            </Col>
                                            {fundingServiceParts.length > 0 && (
                                                <Col span={1}>
                                                    {fundingSetting.sendInvoice && (
                                                        <Select
                                                            error={fundingSetting.errors.get('fundingServicePartId')}
                                                            onChange={(option) =>
                                                                handleChange(
                                                                    'fundingServicePartId',
                                                                    option?.value ?? null,
                                                                    index
                                                                )
                                                            }
                                                            options={[
                                                                {
                                                                    label: translateKey(
                                                                        'finance.fundingServicePart.all'
                                                                    ),
                                                                    value: null,
                                                                },
                                                                ...fundingServiceParts.map((part) => ({
                                                                    label: part.name,
                                                                    value: part.id,
                                                                })),
                                                            ]}
                                                            value={{
                                                                label: mapFundablePartIdToName(
                                                                    fundingSetting.fundingServicePartId
                                                                ),
                                                                value: fundingSetting.fundingServicePartId,
                                                            }}
                                                        />
                                                    )}
                                                </Col>
                                            )}
                                            {isEditing && (
                                                <Col span={1}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent={['center', 'flex-end']}
                                                    >
                                                        <IconButton
                                                            icon="delete"
                                                            onClick={() => onRemoveFundingSetting(index)}
                                                            size="medium"
                                                        />
                                                    </Box>
                                                </Col>
                                            )}
                                        </>
                                    )}
                                </Row>
                            </FormSection>
                        );
                    })}

                    {isEditing && (
                        <>
                            <Button
                                as="a"
                                iconLeft="add"
                                id={`automation-add-funding-setting-button-${props.serviceOrganizationSetting.id}`}
                                onClick={() => onAddFundingSetting()}
                                style={{ marginTop: '32px' }}
                                variant="link"
                            >
                                {translateKey('fundingSettings.addFundingSetting')}
                            </Button>

                            <FormButtonRow>
                                <CancelButton
                                    automation={`cancel-button-${props.serviceOrganizationSetting.id}`}
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    automation={`save-button-${props.serviceOrganizationSetting.id}`}
                                    disabled={isSaving}
                                    onSave={async () =>
                                        isConfirmationRequired() ? setShowConfirmationModal(true) : await handleSubmit()
                                    }
                                />
                            </FormButtonRow>
                        </>
                    )}

                    <ConfirmationModal
                        cancelText={translateKey('common.cancelling')}
                        confirmText={translateKey('common.proceed')}
                        handleNo={() => setShowConfirmationModal(false)}
                        handleYes={() => {
                            setShowConfirmationModal(false);
                            handleSubmit();
                        }}
                        isOpen={showConfirmationModal}
                        question="Deze wijziging heeft financiële gevolgen op reeds bekostigde processen. Neem contact op met Finance."
                        title={translateKey('common.notice')}
                    />
                </Form>
            </Card.Body>
        </Card>
    );

    function convertDateValue(value?: string | Date | null) {
        if (value) {
            const newDate = DateTime.from(value);

            if (newDate) {
                return newDate.toDate();
            }
        }

        // For startDate we need a default value, for endDate we want to allow null
        return null;
    }

    function renderTooltip(tooltipText: string) {
        return (
            <Tooltip content={tooltipText}>
                <Icon
                    color="primary"
                    icon="information"
                    size={12}
                />
            </Tooltip>
        );
    }

    async function load() {
        const url = `/api/fundingSettings?serviceOrganizationSettingId=${props.serviceOrganizationSetting.id}&orderByStartDate=true&orderByStartDateDirection=DESC`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        if (result.json?.items?.length > 0) {
            setOldFundingSettings(result.json.items);
            const fundingSettingsWithErrors = result.json.items.map((item: IFundingSetting) => ({
                ...item,
                errors: emptyValidationErrors,
            }));
            setFundingSettings(fundingSettingsWithErrors);
        }

        await loadFundingServiceParts();
    }

    function handleChange<K extends keyof IFundingSettingWithErrors>(
        name: K,
        value: IFundingSettingWithErrors[K],
        index: number
    ) {
        const newFundingSettings = [...fundingSettings];
        const fundingSettingToUpdate = { ...newFundingSettings[index] };
        fundingSettingToUpdate[name] = value;

        const schema = fundingSettingToUpdate.id ? fundingSettingsAddSchema : fundingSettingsEditSchema;
        fundingSettingToUpdate.errors = updateValidationErrorsForOne(
            schema,
            fundingSettingToUpdate,
            fundingSettingToUpdate.errors,
            name
        );
        newFundingSettings[index] = fundingSettingToUpdate;
        setFundingSettings(newFundingSettings);
    }

    function handleChangeFundingMethod(
        {
            isAlternativeFinancing = false,
            sendInvoice = false,
        }: { isAlternativeFinancing?: boolean; sendInvoice?: boolean },
        index: number
    ) {
        const newFundingSettings = [...fundingSettings];
        const fundingSettingToUpdate = { ...newFundingSettings[index], isAlternativeFinancing, sendInvoice };

        if (!sendInvoice) {
            fundingSettingToUpdate.compensationCharge = null;
            fundingSettingToUpdate.fundingServicePartId = null;
        }

        const schema = fundingSettingToUpdate.id ? fundingSettingsAddSchema : fundingSettingsEditSchema;

        fundingSettingToUpdate.errors = updateValidationErrorsForMany(
            schema,
            fundingSettingToUpdate,
            fundingSettingToUpdate.errors,
            ['isAlternativeFinancing', 'sendInvoice']
        );

        newFundingSettings[index] = fundingSettingToUpdate;

        setFundingSettings(newFundingSettings);
    }

    function onRemoveFundingSetting(index: number) {
        const updatedFundingSettings = [...fundingSettings];
        updatedFundingSettings.splice(index, 1);
        setFundingSettings(updatedFundingSettings);
    }

    function onAddFundingSetting() {
        setFundingSettings([
            ...fundingSettings,
            {
                compensationCharge: null,
                endDate: null,
                errors: emptyValidationErrors,
                financedByPreviousService: false,
                fundingServicePartId: null,
                isAlternativeFinancing: false,
                sendInvoice: false,
                startDate: DateTime.now().format('YYYY-MM-DD'),
            },
        ]);
    }

    function handleCancel() {
        setIsSaving(false);
        setIsEditing(false);
        setFundingSettings(
            oldFundingSettings.map((item: IFundingSetting) => ({ ...item, errors: emptyValidationErrors }))
        );
    }

    function validateFundingSetting(fundingSetting: IFundingSettingWithErrors) {
        const schema = fundingSetting.id ? fundingSettingsAddSchema : fundingSettingsEditSchema;

        return validate(schema, fundingSetting);
    }

    function validateFundingSettings(items: IFundingSettingWithErrors[]) {
        const fundingSettingsToValidate = [...items];
        let hasErrors = false;
        for (const fundingSetting of fundingSettingsToValidate) {
            fundingSetting.errors = emptyValidationErrors;

            const validationResult = validateFundingSetting(fundingSetting);
            if (validationResult.hasErrors) {
                hasErrors = true;
                fundingSetting.errors = validationResult.errors;
            }
        }
        if (!hasErrors) {
            const overlapValidatedItems = [];
            for (const item of fundingSettingsToValidate) {
                const hasFundablePart = item.fundingServicePartId !== null;
                const itemsThatMaybeOverlap = items.filter(
                    (otherItem) => otherItem !== item && (!hasFundablePart || otherItem.fundingServicePartId === null)
                );
                const [overlapValidatedItem] = validateDatetimeOverlap([item, ...itemsThatMaybeOverlap]);
                overlapValidatedItems.push(overlapValidatedItem);
            }

            return overlapValidatedItems;
        }

        return fundingSettingsToValidate;
    }

    async function handleSubmit() {
        setIsSaving(true);

        const validatedFundingSettings = validateFundingSettings(fundingSettings);
        const hasErrors = validatedFundingSettings.some((fundingSetting) =>
            fundingSetting.errors.some((error) => error !== undefined)
        );

        if (hasErrors) {
            setFundingSettings(validatedFundingSettings);
        } else {
            const newFundingSettings = validatedFundingSettings.map((fundingSetting) => {
                return { ...omit(fundingSetting, 'errors') };
            });

            await abortableFetch.fetchJson('/api/fundingSettings/updateAll', 'POST', {
                serviceOrganizationSettingId: props.serviceOrganizationSetting.id,
                fundingSettings: newFundingSettings,
            });

            setIsEditing(false);
            setOldFundingSettings(
                fundingSettings.map((fundingSetting) => {
                    return { ...fundingSetting };
                })
            );
            props.onSubmit();
            load();
        }
        setIsSaving(false);
    }

    function isConfirmationRequired() {
        const now = DateTime.now();

        for (const newFundingSetting of fundingSettings) {
            const oldFundingSetting = oldFundingSettings.find(
                (fundingSetting) => fundingSetting.id === newFundingSetting.id
            );
            if (oldFundingSetting?.endDate) {
                if (DateTime.from(oldFundingSetting.endDate).isBefore(now)) {
                    // updating a fundingsettings record (e.g price change, start date, end date, etc) with an end date in the past
                    return true;
                } else if (
                    DateTime.from(newFundingSetting.startDate).isBefore(oldFundingSetting.startDate) &&
                    DateTime.from(newFundingSetting.startDate).isBefore(now)
                ) {
                    // updating a fundingsettings record with a start date in the past to an earlier date
                    return true;
                }
            } else if (!oldFundingSetting && DateTime.from(newFundingSetting.startDate).isBefore(now)) {
                // adding a fundingsettings record with a start date in the past
                return true;
            }
        }

        for (const oldFundingSetting of oldFundingSettings) {
            const newFundingSetting = fundingSettings.find(
                (fundingSetting) => fundingSetting.id === oldFundingSetting.id
            );
            if (!newFundingSetting && DateTime.from(oldFundingSetting.startDate).isBefore(now)) {
                // deleting a fundingsettings record with a start date in the past
                return true;
            }
        }

        return false;
    }

    async function loadFundingServiceParts() {
        const serviceId = props.serviceOrganizationSetting.service.id;
        const parts = await getFundingServiceParts(serviceId as Uuid, abortSignal);
        parts.sort((a, b) => a.name.localeCompare(b.name));
        setFundingServiceParts(parts);
    }

    function mapFundablePartIdToName(id: string | null) {
        if (id === null) {
            return translateKey('finance.fundingServicePart.all');
        }

        return fundingServiceParts.find((part) => part.id === id)?.name ?? '?';
    }
}
